// src/stores/adStore.js
import { defineStore } from "pinia";
import { ref } from "vue";
import { useVideoStore } from "./videoStore";
import { replaceMacrosDynamically } from "../utils/replaceMacros";
import { VASTClient } from "@dailymotion/vast-client";


const dataStoreUrl = import.meta.env.VITE_DATA_STORE_URL;

export const useAdStore = defineStore("adStore", () => {
  const intervalMs = ref(0);
  const GET = 2;
  const HEAD = 1;
  const NULL = 0;
  const requestMethod = ref(HEAD);
  const headIntervalId = ref(null);
  const videoToLookFor = ref(null);
  const storedAdKeys = ref([]);

  const mappingRequestMethod = {
    [NULL]: null,
    [HEAD]: "HEAD",
    [GET]: "GET"
  };
  function setupAdConfig(intervalFromFeed) {
    intervalMs.value = intervalFromFeed;
    startInterval();
  }

  function setupRequestMethod(method) {
    if ([NULL, HEAD, GET].includes(method)) {
      requestMethod.value = method;
    } else {
      console.error("Invalid request method:", method);
    }
  }

  function setupVideoToLookFor(video) {
    videoToLookFor.value = video;
    storedAdKeys.value = [];
  }

  function startInterval() {
    stopInterval();
    if (!intervalMs.value || intervalMs.value <= 0) {
      return;
    }
    performRequest();

    headIntervalId.value = setInterval(() => {
      performRequest();
    }, intervalMs.value);
  }

  function stopInterval() {
    if (headIntervalId.value) {
      clearInterval(headIntervalId.value);
      headIntervalId.value = null;
    }
  }

  function getUrl() {

    const videoStore = useVideoStore();
    let firstVastUrl = null;
    let adBreaks = [];

    if (videoToLookFor.value !== null) {
      firstVastUrl = videoToLookFor.value.content.vastTagUrl[0];
      adBreaks = videoToLookFor.value.content.adBreaks || [];
    } else {
      // Find first VAST URL using functional programming approach
      // Using find instead of reduce for better performance
      const singleVideoSection = videoStore.videos.find(
        (section) =>
          section.type === "singlevideo" &&
          section.videos?.length > 0 &&
          section.videos.some((vid) => vid.content?.vastTagUrl)
      );

      if (singleVideoSection) {
        const firstVideo = singleVideoSection.videos.find(
          (vid) => vid.content?.vastTagUrl
        );
        //TODO: change this line for vastTagUrl[0]
        firstVastUrl = firstVideo.content.vastTagUrl[0];
        adBreaks = firstVideo.content.adBreaks || [];
      } else {
        // Look for playlist type if no singlevideo found
        const playlistSection = videoStore.videos.find(
          (section) => section.type === "playlist" && section.series?.length > 0
        );

        if (playlistSection) {
          // Use optional chaining and flattening to simplify the nested search
          const firstEpisodeWithVast = playlistSection.series
            ?.flatMap((series) => series.seasons || [])
            ?.flatMap((season) => season.episodes || [])
            ?.find((episode) => episode.content?.vastTagUrl);

          if (firstEpisodeWithVast) {
            firstVastUrl = firstEpisodeWithVast.content.vastTagUrl;
            adBreaks = firstEpisodeWithVast.content.adBreaks || [];
          }
        }
      }
    }

    if (!firstVastUrl) {
      console.error("No VAST URL found");
      return null;
    }
    const originalUrlVast = firstVastUrl;
    const width = window.innerWidth || document.documentElement.clientWidth || 1920;
    const height = window.innerHeight || document.documentElement.clientHeight || 1080;
    const autoPlay = false;
    const mute = false;
    const skip = false;
    const finalUrl = replaceMacrosDynamically(originalUrlVast, width, height, autoPlay, mute, skip, adBreaks);
    return finalUrl;
  }

  async function processResponse(adResponse) {
    if (adResponse.ok) {
      if (requestMethod.value == GET) {
        const xmlResponse = await adResponse.text();
        const newKey = await storeServedVast(xmlResponse);

        if (newKey) {
          storedAdKeys.value.push(newKey);
        }
      }
    }
  }

  async function performRequest() {
    if (!requestMethod.value) return;
    const method = mappingRequestMethod[requestMethod.value];
    try {
      const finalUrl = getUrl();
      const response = await fetch(finalUrl, { method });
      processResponse(response);
    } catch (err) {
      console.error(`${method} request failed:`, err);
    }
  }

  async function storeServedVast(payload) {
    try {
      if (!payload) {
        return null;
      }

      // check if the payload is a valid xml and have content
      const vastParser = new VASTClient();
      const xparser = new DOMParser();
      const doc = xparser.parseFromString(payload, "application/xml");

      const vastResponse = await vastParser.parseVAST(doc);
      
      if (vastResponse.ads.length === 0) {
        console.error("No ads in VAST response");
        return null;
      }
      
      const response = await fetch(`${dataStoreUrl}/storage`, {
        method: "POST",
        headers: { "Content-Type": "text/xml" },
        body: payload
      });
      if (!response.ok) {
        console.error("Storage POST error:", response.status);
        return null;
      }
      const data = await response.json();
      return {...data, createdAt: new Date()};
    } catch (err) {
      console.error("Error storing data in data-store:", err);
      return null;
    }
  }

  async function getStoredVastByKey(theKey) {
    if (!theKey) return null;
    try {
      const response = await fetch(`${dataStoreUrl}/storage/${theKey}`, {
        method: "GET"
      });
      if (!response.ok) {
        console.error("Error getting stored data:", response.status);
        return null;
      }
      return await response.json();
    } catch (err) {
      console.error("Error retrieving data from data-store:", err);
      return null;
    }
  }

  function getStoredAdsUrlsValid() {
    const now = new Date();
    const validAds = storedAdKeys.value.filter(ad => {
      const adAge = now - ad.createdAt;
      return adAge < ad.expirems;
    }).map(ad => {
      return ad.url.replace(/^(?!https?:\/\/)/i, 'https://');
    });
    return validAds;
  }

  return {
    intervalMs,
    setupAdConfig,
    setupRequestMethod,
    setupVideoToLookFor,
    startInterval,
    stopInterval,
    storeServedVast,
    getStoredVastByKey,
    performRequest,
    getUrl,
    requestMethods: {
      GET,
      HEAD,
      NULL
    },
    requestMethod,
    getStoredAdsUrlsValid
  };
});
